import { keepPreviousData, useMutation, useQuery } from "@tanstack/react-query";
import { queryClient } from "@/shared/services/queryClient";
import { toast } from "sonner";
import {
  transportRatesApi,
  type CreateTransportRateRequest,
  type UpdateTransportRateRequest,
} from "../services/transportRatesApi";

const QK = {
  all: () => ["transportRates"] as const,
  active: () => ["transportRates", "active"] as const,
} as const;

// ── Queries ────────────────────────────────────────────────────────────────

export const useTransportRates = (pageNumber: number, search: string) =>
  useQuery({
    queryKey: [...QK.all(), pageNumber, search],
    queryFn: () => transportRatesApi.getPaged(pageNumber, search),
    placeholderData: keepPreviousData,
  });

export const useActiveTransportRates = () =>
  useQuery({
    queryKey: QK.active(),
    queryFn: () => transportRatesApi.getActive(),
  });

// ── Mutations ─────────────────────────────────────────────────────────────

export const useCreateTransportRate = () =>
  useMutation({
    mutationFn: (data: CreateTransportRateRequest) => transportRatesApi.create(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["transportRates"] });
      toast.success("Transport rate created.");
    },
    onError: (err: Error) => {
      toast.error(err.message ?? "Failed to create transport rate.");
    },
  });

export const useUpdateTransportRate = () =>
  useMutation({
    mutationFn: ({ id, data }: { id: string; data: UpdateTransportRateRequest }) =>
      transportRatesApi.update(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["transportRates"] });
      toast.success("Transport rate updated.");
    },
    onError: (err: Error) => {
      toast.error(err.message ?? "Failed to update transport rate.");
    },
  });

export const useDeleteTransportRate = () =>
  useMutation({
    mutationFn: (id: string) => transportRatesApi.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["transportRates"] });
      toast.success("Transport rate removed.");
    },
    onError: (err: Error) => {
      toast.error(err.message ?? "Failed to remove transport rate.");
    },
  });
